'use client'
import { useRef, useState } from 'react'

/**
 * MasjidPhotosField — optional building/signboard/interior photos for the web
 * registration wizard. Up to `max` images, each uploaded to /api/upload
 * (context=masjidGallery, server-side Cloudinary keys) the moment it's picked.
 * Surfaces only the successfully-uploaded secure_urls to the parent via onChange.
 *
 * HONEST: a demo/placeholder response (Cloudinary unconfigured → { demo:true }) is
 * treated as a FAILURE — never attached as a real photo. Optional + non-blocking:
 * a failed or in-flight upload simply isn't included, and registration still works
 * with zero photos.
 */

type Status = 'uploading' | 'done' | 'error'
type Item = { key: string; file?: File; preview: string | null; url: string | null; status: Status; error?: string }

interface Props {
  value: string[]
  onChange: (urls: string[]) => void
  max?: number
  /* Hero picker (opt-in): when onCover is provided, each photo shows a "hero" toggle and the
     chosen url is reported back. The convention remains photos[0] when nothing is chosen. */
  cover?: string
  onCover?: (url: string) => void
}

export default function MasjidPhotosField({ value, onChange, max = 10, cover, onCover }: Props) {
  // Initialise from already-uploaded urls so thumbnails persist across wizard steps.
  const [items, setItems] = useState<Item[]>(() =>
    value.map((u, i) => ({ key: `init-${i}`, preview: null, url: u, status: 'done' as Status })),
  )
  const nextId = useRef(0)
  const inputRef = useRef<HTMLInputElement>(null)
  // Separate input for camera capture — `capture="environment"` opens the rear camera on mobile
  // browsers; on desktop the attribute is ignored and it falls back to the normal file chooser.
  const cameraRef = useRef<HTMLInputElement>(null)

  const emit = (next: Item[]) => onChange(next.filter((x) => x.status === 'done' && x.url).map((x) => x.url as string))

  async function uploadOne(key: string, file: File) {
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('folder', 'masjids/onboarding')
      form.append('context', 'masjidGallery')
      const res = await fetch('/api/upload', { method: 'POST', body: form })
      const data = await res.json().catch(() => ({} as any))
      if (!res.ok || !data?.url || data?.demo) {
        const error = data?.error || (data?.demo ? 'Photo storage isn’t enabled on the server.' : 'Upload failed.')
        setItems((prev) => prev.map((x) => (x.key === key ? { ...x, status: 'error', error } : x)))
        return
      }
      setItems((prev) => {
        const next = prev.map((x) => (x.key === key ? { ...x, status: 'done' as Status, url: data.url } : x))
        emit(next)
        return next
      })
    } catch {
      setItems((prev) => prev.map((x) => (x.key === key ? { ...x, status: 'error', error: 'Upload failed. Please try again.' } : x)))
    }
  }

  function addFiles(files: FileList | null) {
    if (!files || !files.length) return
    const remaining = max - items.length
    const picked = Array.from(files).slice(0, Math.max(0, remaining))
    if (!picked.length) return
    const created = picked.map((file) => ({ key: `p${nextId.current++}`, file, preview: URL.createObjectURL(file), url: null, status: 'uploading' as Status }))
    setItems((prev) => [...prev, ...created])
    created.forEach((c) => uploadOne(c.key, c.file))
  }

  function remove(key: string) {
    setItems((prev) => {
      const removed = prev.find((x) => x.key === key)
      const next = prev.filter((x) => x.key !== key)
      emit(next)
      // If the hero photo was removed, fall the hero back to the new first uploaded photo.
      if (onCover && removed?.url && removed.url === cover) {
        const firstDone = next.find((x) => x.status === 'done' && x.url)?.url || ''
        onCover(firstDone)
      }
      return next
    })
  }

  function retry(key: string) {
    setItems((prev) => prev.map((x) => (x.key === key ? { ...x, status: 'uploading' as Status, error: undefined } : x)))
    const it = items.find((x) => x.key === key)
    if (it?.file) void uploadOne(key, it.file)
  }

  const full = items.length >= max
  const failed = items.filter((x) => x.status === 'error').length
  // Effective hero: the explicit choice, else the first successfully-uploaded photo (photos[0]).
  const firstDoneUrl = items.find((x) => x.status === 'done' && x.url)?.url || ''
  const heroUrl = (onCover && cover) || firstDoneUrl

  const tile: React.CSSProperties = { width: 88, height: 88, borderRadius: 10, overflow: 'hidden', position: 'relative', background: '#f3f4f6', border: '1px solid #e8e8e8', flex: '0 0 auto' }

  return (
    <div className="entry-field">
      <span>
        Photos <span style={{ fontWeight: 400, color: '#999' }}>· optional, up to {max} · {items.length}/{max}</span>
      </span>
      <p style={{ fontSize: 12, color: '#777', margin: '2px 0 8px' }}>
        Add the signboard, building or interior — a photo helps your masjid stand out in the directory.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {items.map((it) => (
          <div key={it.key} style={tile}>
            {it.preview || it.url ? (
              <img src={(it.preview || it.url) as string} alt="Masjid photo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : null}

            {it.status === 'uploading' && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 700 }}>
                Uploading…
              </div>
            )}
            {it.status === 'error' && (
              <button
                type="button"
                onClick={() => retry(it.key)}
                title={it.error || 'Upload failed'}
                style={{ position: 'absolute', inset: 0, background: 'rgba(176,0,32,0.55)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700 }}
              >
                ↻ Retry
              </button>
            )}

            <button
              type="button"
              onClick={() => remove(it.key)}
              aria-label="Remove photo"
              style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: 10, border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', cursor: 'pointer', fontSize: 12, lineHeight: '20px', padding: 0 }}
            >
              ×
            </button>

            {/* Hero picker — only when the parent opts in (onCover) and this photo has uploaded. */}
            {onCover && it.status === 'done' && it.url && (
              it.url === heroUrl ? (
                <span
                  aria-label="Hero photo"
                  style={{ position: 'absolute', bottom: 4, left: 4, background: 'rgba(14,124,97,0.92)', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 6, padding: '2px 6px' }}
                >★ Hero</span>
              ) : (
                <button
                  type="button"
                  onClick={() => onCover(it.url as string)}
                  title="Set as the hero (main) photo"
                  style={{ position: 'absolute', bottom: 4, left: 4, background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 700, borderRadius: 6, padding: '2px 6px' }}
                >☆ Hero</button>
              )
            )}
          </div>
        ))}

        {!full && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            style={{ ...tile, border: '2px dashed #d8d8d4', background: '#f8f8f6', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, color: '#0E7C61' }}
          >
            <span style={{ fontSize: 22, lineHeight: 1 }}>＋</span>
            <span style={{ fontSize: 11, fontWeight: 700 }}>Add</span>
          </button>
        )}

        {!full && (
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            title="Take a photo with your camera"
            style={{ ...tile, border: '2px dashed #d8d8d4', background: '#f8f8f6', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, color: '#0E7C61' }}
          >
            <span style={{ fontSize: 20, lineHeight: 1 }}>📷</span>
            <span style={{ fontSize: 11, fontWeight: 700 }}>Camera</span>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          addFiles(e.target.files)
          e.currentTarget.value = ''
        }}
      />
      {/* Camera capture: single-shot; `capture="environment"` = rear camera on mobile browsers.
          Desktop browsers ignore `capture` and open the normal file chooser (graceful fallback). */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => {
          addFiles(e.target.files)
          e.currentTarget.value = ''
        }}
      />

      {failed > 0 && (
        <div style={{ fontSize: 12, color: '#dc2626', marginTop: 6 }}>
          ⚠️ {failed} photo{failed > 1 ? 's' : ''} didn’t upload — retry, or remove and submit without {failed > 1 ? 'them' : 'it'}. Photos are optional.
        </div>
      )}
    </div>
  )
}
